import React, { useState } from "react";
import { FUND_CANDIDATES, HORIZON_OPTIONS, CONTRIBUTION_OPTIONS } from "./data.js";

const ACCENT_CLASSES = {
  lime: { border: "border-lime", bg: "bg-lime/10", text: "text-lime" },
  gold: { border: "border-gold", bg: "bg-gold/10", text: "text-gold" },
  danger: { border: "border-danger", bg: "bg-danger/10", text: "text-danger" },
};
const accentByRisk = { LOW: "lime", MEDIUM: "gold", HIGH: "danger" };

export default function PlanningStage({ onDeploy }) {
  const [accountType, setAccountType] = useState(null); // 'taxable' | 'tax-advantaged'
  const [vehicleId, setVehicleId] = useState(null);
  const [monthlyContribution, setMonthlyContribution] = useState(150);
  const [reinvestDividends, setReinvestDividends] = useState(true);
  const [horizonYears, setHorizonYears] = useState(20);

  const canDeploy = !!accountType && !!vehicleId;

  const handleDeploy = () => {
    if (!canDeploy) return;
    onDeploy({ accountType, vehicleId, monthlyContribution, reinvestDividends, horizonYears });
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>CHART YOUR COURSE</span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Four choices set the terms of the whole quest. Time is the one lever you can't get back
        later — the earlier this money starts working, the more it can grow.
      </p>

      {/* Account type */}
      <section className="panel mb-4 p-4">
        <p className="mb-2 font-display text-[10px] text-cyan">ACCOUNT TYPE</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <button
            onClick={() => setAccountType("tax-advantaged")}
            className={`border-2 px-3 py-3 text-left text-[11px] transition-colors ${
              accountType === "tax-advantaged" ? "border-lime bg-lime/10 text-lime" : "border-mist/30 text-mist/70 hover:border-lime"
            }`}
          >
            <span className="block font-display text-[9px] uppercase">Roth IRA / ISA</span>
            <span className="text-mist/50">Tax-advantaged — growth compounds without tax friction along the way.</span>
          </button>
          <button
            onClick={() => setAccountType("taxable")}
            className={`border-2 px-3 py-3 text-left text-[11px] transition-colors ${
              accountType === "taxable" ? "border-gold bg-gold/10 text-gold" : "border-mist/30 text-mist/70 hover:border-gold"
            }`}
          >
            <span className="block font-display text-[9px] uppercase">Standard Taxable Account</span>
            <span className="text-mist/50">No special shelter — some drag from taxes along the way.</span>
          </button>
        </div>
      </section>

      {/* Fund vehicle */}
      <section className="panel mb-4 p-4">
        <p className="mb-2 font-display text-[10px] text-magenta">FUND VEHICLE</p>
        <div className="space-y-2">
          {FUND_CANDIDATES.map((f) => {
            const selected = vehicleId === f.id;
            const accent = ACCENT_CLASSES[accentByRisk[f.correctRisk]];
            return (
              <button
                key={f.id}
                onClick={() => setVehicleId(f.id)}
                className={`w-full border-2 px-3 py-3 text-left text-[11px] transition-colors ${
                  selected ? `${accent.border} ${accent.bg}` : "border-mist/20 hover:border-mist/50"
                }`}
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-display text-[9px] uppercase text-mist">{f.name}</span>
                  <span className={accent.text}>~{f.netAnnualReturn}%/yr</span>
                </div>
                <p className="text-mist/50">{f.flavor}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Contribution */}
      <section className="panel mb-4 p-4">
        <p className="mb-2 font-display text-[10px] text-gold">MONTHLY CONTRIBUTION</p>
        <p className="mb-3 text-[11px] text-mist/50">
          Dollar-cost averaging: the same amount, every month, whether the market's up or down.
        </p>
        <div className="flex flex-wrap gap-2">
          {CONTRIBUTION_OPTIONS.map((amt) => (
            <button
              key={amt}
              onClick={() => setMonthlyContribution(amt)}
              className={`border-2 px-4 py-2 font-display text-[10px] transition-colors ${
                monthlyContribution === amt ? "border-gold bg-gold text-ink" : "border-mist/30 text-mist/70 hover:border-gold"
              }`}
            >
              ${amt}
            </button>
          ))}
        </div>
      </section>

      {/* Dividend reinvestment */}
      <section className="panel mb-4 p-4">
        <p className="mb-2 font-display text-[10px] text-cyan">DIVIDEND REINVESTMENT</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setReinvestDividends(true)}
            className={`border-2 px-3 py-2 font-display text-[9px] uppercase transition-colors ${
              reinvestDividends ? "border-cyan bg-cyan text-ink" : "border-mist/30 text-mist/70 hover:border-cyan"
            }`}
          >
            Reinvest
          </button>
          <button
            onClick={() => setReinvestDividends(false)}
            className={`border-2 px-3 py-2 font-display text-[9px] uppercase transition-colors ${
              !reinvestDividends ? "border-mist bg-mist/20 text-mist" : "border-mist/30 text-mist/70 hover:border-mist"
            }`}
          >
            Take as Cash
          </button>
        </div>
        <p className="mt-2 text-[11px] text-mist/50">
          Reinvesting uses each payout to buy more shares automatically — a quiet accelerant on
          the compounding underneath everything else.
        </p>
      </section>

      {/* Time horizon */}
      <section className="panel mb-6 p-4">
        <p className="mb-2 font-display text-[10px] text-magenta">TIME HORIZON</p>
        <p className="mb-3 text-[11px] text-mist/50">
          Starting early is the single biggest lever in this whole game — time is what lets the
          snowball effect take hold.
        </p>
        <div className="grid grid-cols-3 gap-2">
          {HORIZON_OPTIONS.map((y) => (
            <button
              key={y}
              onClick={() => setHorizonYears(y)}
              className={`border-2 px-3 py-2 font-display text-[10px] transition-colors ${
                horizonYears === y ? "border-magenta bg-magenta text-ink" : "border-mist/30 text-mist/70 hover:border-magenta"
              }`}
            >
              {y} yrs
            </button>
          ))}
        </div>
      </section>

      <button onClick={handleDeploy} disabled={!canDeploy} className="btn-magenta w-full disabled:opacity-30">
        {canDeploy ? "BEGIN THE QUEST ▶" : "CHOOSE AN ACCOUNT AND A VEHICLE"}
      </button>
    </div>
  );
}
